import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { Tag, Spinner, FlexGroup, Button } from '@zenqms/zui';
import { ToggleVisibilityButton, SignatureCard } from '@zenqms/design-system';
import SignatureSideBar from '../issue/SignatureSideBar';
import { PDFtron } from '../document/PDFTron';
import { fetchDocumentData } from '../document/documents-slice';
import DocumentModel from '../document/DocumentModel';
import { FORMAT_TYPE, formatDate } from '../utils/moment';
import ESignDisclosureModal from '../issue/ESignDisclosureModal';
import { Popup } from 'semantic-ui-react';

const WORKFLOW_STEP_STATUSES = {
  waiting: 'waiting',
  inProgress: 'in_progress',
  completed: 'completed',
  rejected: 'rejected',
};

export function ViewDocumentPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showApproveSignatureModal, setShowApproveSignatureModal] = useState(false);
  const [showRejectSignatureModal, setShowRejectSignatureModal] = useState(false);
  const [hideSignatures, setHideSignatures] = useState(false);
  const itemAssignmentCode = useSelector((state) => state.auth.user.itemAssignmentCode);
  const tosStatus = useSelector((state) => state.documents.document.tosStatus);
  const isLoading = useSelector((state) => state.documents.isLoading);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const documentApiResponse = useSelector((state) => {
    return state.documents.document;
  });

  useEffect(() => {
    dispatch(fetchDocumentData({ itemAssignmentCode, navigate }));
  }, [itemAssignmentCode]);

  const document = () => {
    return new DocumentModel({ documentData: documentApiResponse });
  };

  const handleApproval = () => {
    setShowApproveSignatureModal((prevState) => !prevState);
    setShowRejectSignatureModal(false);
  };

  const handleReject = () => {
    setShowRejectSignatureModal((prevState) => !prevState);
    setShowApproveSignatureModal(false);
  };

  const handleCloseSignatureModal = () => {
    setShowApproveSignatureModal(false);
    setShowRejectSignatureModal(false);
  };

  const toggleHideSignatures = () => {
    setHideSignatures(!hideSignatures);
  };

  const intl = useIntl();

  const getWorkflowName = (type, subtype) => {
    switch (type) {
      case 'approve':
        switch (subtype) {
          case null:
            return intl.formatMessage({
              id: 'documentPage.workflowName.approvalMajor',
              defaultMessage: 'Approval - Major Revision',
            });
          default:
            return '';
        }
      case 'retire':
        return intl.formatMessage({ id: 'documentPage.workflowName.retire', defaultMessage: 'Retirement' });
      case 'minor_revision':
        return intl.formatMessage({
          id: 'documentPage.workflowName.approvalMinor',
          defaultMessage: 'Approval - Minor Revision',
        });
      default:
        return '';
    }
  };

  const getStepStatus = (step) => {
    const externalStatusMap = {
      [WORKFLOW_STEP_STATUSES.inProgress]: {
        EMAIL_SENT: { id: 'documentPage.signatureStatuses.zensign_email_sent', date: step.updatedAt },
        ITEM_VIEWED: { id: 'documentPage.signatureStatuses.zensign_item_viewed', date: step.updatedAt },
        WAITING_FOR_PREVIOUS: { id: 'documentPage.signatureStatuses.zensign_waiting_prior_steps' },
      },
      [WORKFLOW_STEP_STATUSES.waiting]: {
        WAITING_FOR_PREVIOUS: { id: 'documentPage.signatureStatuses.zensign_waiting_prior_steps' },
      },
      [WORKFLOW_STEP_STATUSES.completed]: {
        COMPLETED: { id: 'documentPage.signatureStatuses.zensign_completed', date: step.completedAt },
      },
      [WORKFLOW_STEP_STATUSES.rejected]: {
        REJECTED: { id: 'documentPage.signatureStatuses.zensign_rejected', date: step.rejectedAt },
      },
    };

    if (step.isExternalAssignment) {
      const externalStepStatus = externalStatusMap[step.status][step.externalStepStatus] || { id: '-' };
      const { id, date } = externalStepStatus;
      return <FormattedMessage id={id} values={{ date: date ? formatDate(date, FORMAT_TYPE.timeDate) : '' }} />;
    } else {
      switch (step.status) {
        case WORKFLOW_STEP_STATUSES.inProgress:
          return <FormattedMessage alert-sent id="documentPage.signatureStatuses.alert_sent" />;
        case WORKFLOW_STEP_STATUSES.waiting:
          return <FormattedMessage id="documentPage.signatureStatuses.waiting_prior_steps" />;
        case WORKFLOW_STEP_STATUSES.completed: {
          if (step.step.type === 'signature') {
            return (
              <FormattedMessage
                completed-signature
                id="documentPage.signatureStatuses.completed"
                values={{ date: formatDate(step.completedAt, FORMAT_TYPE.timeDate) }}
              />
            );
          }
          return (
            <FormattedMessage
              completed-view
              id="documentPage.signatureStatuses.viewed"
              values={{ date: formatDate(step.completedAt, FORMAT_TYPE.timeDate) }}
            />
          );
        }
        default:
          return (
            <FormattedMessage
              rejected-signature
              id="documentPage.signatureStatuses.rejected"
              values={{ date: formatDate(step.rejectedAt, FORMAT_TYPE.timeDate) }}
            />
          );
      }
    }
  };

  const getUserName = (step) => {
    return step.signature
      ? step.userFullName
      : step.userIsActive || step.isExternalAssignment
      ? step.userFullName
      : `${step.userFullName} *${intl
          .formatMessage({ id: 'common.disabled', defaultMessage: 'disabled' })
          .toUpperCase()}*`;
  };

  return isLoading ? (
    <FlexGroup justifyContent="center">
      <Spinner size="xl" />
    </FlexGroup>
  ) : (
    <div className="view-document-page">
      <ESignDisclosureModal tosNotAccepted={tosStatus === 'NotAccepted'} issueType={'document'}></ESignDisclosureModal>
      <div className="document-header">
        <Popup
          content={document()?.documentName()}
          hideOnScroll={true}
          position={'bottom center'}
          size={'mini'}
          trigger={<span className="title">{document()?.documentName()}</span>}
        />
        <Tag variant={document()?.statusTagVariant()}>{document()?.documentStatus()}</Tag>
      </div>
      <div className="content-wrapper">
        <div className={`document-viewer ${hideSignatures ? 'fullWidth' : ''}`}>
          <PDFtron
            watermarkOptions={document()?.watermarkOptions()}
            fileExtension={document()?.fileExtension()}
            url={document()?.url()}
          ></PDFtron>
        </div>
        <div className={`signatures ${hideSignatures ? 'hide' : ''}`}>
          <div className="signatures-header">
            <ToggleVisibilityButton onClick={toggleHideSignatures} isOpen={!hideSignatures}>
              {hideSignatures ? (
                <FormattedMessage id="common.show" defaultMessage="Show" />
              ) : (
                <FormattedMessage id="common.hide" defaultMessage="Hide" />
              )}
            </ToggleVisibilityButton>
            {!hideSignatures && document().currentWorkflowStepStatus() === WORKFLOW_STEP_STATUSES.inProgress && (
              <>
                <Button variant="positive" onClick={handleApproval}>
                  <FormattedMessage id="common.approve" defaultMessage="Approve" />
                </Button>
                <Button variant="negative" onClick={handleReject}>
                  <FormattedMessage id="common.reject" defaultMessage="Reject" />
                </Button>
              </>
            )}
          </div>
          {!hideSignatures && (
            <>
              <div className="title">
                <FormattedMessage
                  id="documentPage.signaturesTitle"
                  defaultMessage="Signatures/Reviews"
                  values={{ workflowName: getWorkflowName(document()?.workflowType(), document()?.workflowSubType()) }}
                />
              </div>
              <div>
                {document()
                  ?.workflowSteps()
                  ?.slice()
                  .sort((a, b) => a.order - b.order)
                  ?.map((workflowStep) => (
                    <div key={workflowStep.id} className="signature-card">
                      <SignatureCard
                        key={workflowStep.id}
                        position={
                          workflowStep.step?.alwaysLast ? (
                            <span className="last">
                              <FormattedMessage id="documentPage.last" defaultMessage="LAST" />
                            </span>
                          ) : (
                            workflowStep.order + 1
                          )
                        }
                        status={workflowStep.status}
                      >
                        <SignatureCard.Assignee
                          name={getUserName(workflowStep)}
                          email={workflowStep.userEmail}
                          title={workflowStep.assigneeTitleAtTheTime}
                        />
                        <SignatureCard.Info
                          name={workflowStep.step?.name}
                          reason={workflowStep.signature?.reason}
                          state={
                            [WORKFLOW_STEP_STATUSES.inProgress, WORKFLOW_STEP_STATUSES.waiting].includes(
                              workflowStep.status
                            ) ? (
                              <i>{getStepStatus(workflowStep)}</i>
                            ) : (
                              getStepStatus(workflowStep)
                            )
                          }
                        />
                        {workflowStep.signature?.comment && (
                          <SignatureCard.Comment>{workflowStep.signature?.comment}</SignatureCard.Comment>
                        )}
                      </SignatureCard>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      </div>
      <SignatureSideBar
        signatureReason={document()?.currentStep()?.signatureReason}
        signatureType={'approve'}
        approve={true}
        stageName={intl.formatMessage({ id: 'documentPage.document', defaultMessage: 'Document' })}
        requiresComment={document()?.currentStep()?.requiresComment}
        itemType="document"
        onClose={handleCloseSignatureModal}
        visible={showApproveSignatureModal}
        showSuccessModal={showSuccessModal}
        setShowSuccessModal={setShowSuccessModal}
      ></SignatureSideBar>
      <SignatureSideBar
        signatureReason={document()?.currentStep()?.signatureReason}
        signatureType={'reject'}
        approve={false}
        stageName={intl.formatMessage({ id: 'documentPage.document', defaultMessage: 'Document' })}
        requiresComment={document()?.currentStep()?.requiresComment}
        itemType="document"
        onClose={handleCloseSignatureModal}
        visible={showRejectSignatureModal}
        showSuccessModal={showSuccessModal}
        setShowSuccessModal={setShowSuccessModal}
      ></SignatureSideBar>
    </div>
  );
}
